import { useInviteUserModal } from '../hooks/useInviteUserModal'
import { useLightMode } from '@/hooks/useLightMode'
import { HiOutlineMail } from 'react-icons/hi'

import {
  Box,
  Checkbox,
  CheckboxGroup,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
} from '@chakra-ui/react'

import { Modal } from '@/components/overlays'
import { Button, FormInputControl } from '@/components/ui'

import { ROLE_OPTIONS, RoleExtended } from '../types/types'

interface IProps {
  isOpen: boolean
  onClose: () => void
  preSelectedRole?: RoleExtended
}

export const InviteUserModal = ({
  isOpen,
  onClose,
  preSelectedRole,
}: IProps) => {
  const mode = useLightMode()
  const {
    register,
    errors,
    handleFormSubmit,
    validationRules,
    selectedRoles,
    setSelectedRoles,
    isLoadingInviteUser,
    isSubmitDisabled,
  } = useInviteUserModal({ onClose, preSelectedRole })

  return (
    <Modal
      isOpen={isOpen}
      motionPreset='slideInBottom'
      onClose={onClose}
      size='xl'
    >
      <ModalOverlay />
      <ModalContent bg='#fff'>
        <ModalHeader>
          <Box alignItems='center' display='flex'>
            <HiOutlineMail color='#16357f' size='30px' />
            <Box ml={3}>
              <Box
                color={`text.primary.${mode}`}
                fontSize='26px'
                fontWeight='extrabold'
                letterSpacing='-0.4px'
                lineHeight='1'
              >
                Invite User
              </Box>
            </Box>
          </Box>
        </ModalHeader>
        <ModalCloseButton color={`text.primary.${mode}`} opacity={0.4} />
        <ModalBody p={0}>
          <Box px={6} py={2}>
            <Box
              as='form'
              data-testid='InviteUserModal:form'
              id='invite-user-form'
              onSubmit={handleFormSubmit}
            >
              <Box mb={3}>
                <FormInputControl
                  data-testid='InviteUserModal:firstName'
                  errorMessage={errors.firstName && errors.firstName.message}
                  id='firstName'
                  inputRef={register(validationRules.firstName)}
                  isInvalid={!!errors.firstName}
                  label='First Name'
                  placeholder='Enter First Name'
                />
              </Box>
              <Box mb={3}>
                <FormInputControl
                  data-testid='InviteUserModal:lastName'
                  errorMessage={errors.lastName && errors.lastName.message}
                  id='lastName'
                  inputRef={register(validationRules.lastName)}
                  isInvalid={!!errors.lastName}
                  label='Last Name'
                  placeholder='Enter Last Name'
                />
              </Box>
              <Box mb={3}>
                <FormInputControl
                  data-testid='InviteUserModal:email'
                  errorMessage={errors.email && errors.email.message}
                  id='email'
                  inputRef={register(validationRules.email)}
                  isInvalid={!!errors.email}
                  label='Email'
                  placeholder='Enter Email'
                  type='email'
                />
              </Box>
              <Box mb={3}>
                <Box
                  color={`text.primary.${mode}`}
                  fontSize='14px'
                  fontWeight='semibold'
                  mb={2}
                >
                  Roles (select multiple)
                </Box>
                <CheckboxGroup
                  colorScheme='blue'
                  onChange={(values) => setSelectedRoles(values as string[])}
                  value={selectedRoles}
                >
                  <Stack spacing={2}>
                    {ROLE_OPTIONS.map((option) => (
                      <Checkbox
                        data-testid={`InviteUserModal:role:${option.value}`}
                        key={option.value}
                        value={option.value}
                      >
                        {option.label}
                      </Checkbox>
                    ))}
                  </Stack>
                </CheckboxGroup>
              </Box>
              <Box mb={3}>
                <Checkbox
                  colorScheme='blue'
                  data-testid='InviteUserModal:isSsoUser'
                  name='isSsoUser'
                  ref={register}
                >
                  This user will use SSO (Single Sign-On)
                </Checkbox>
              </Box>
            </Box>
          </Box>
        </ModalBody>
        <ModalFooter py={4}>
          <Button
            data-testid='InviteUserModal:cancel'
            mr={3}
            onClick={onClose}
            variant='secondary'
          >
            Cancel
          </Button>
          <Button
            data-testid='InviteUserModal:submit'
            form='invite-user-form'
            isDisabled={isSubmitDisabled}
            isLoading={isLoadingInviteUser}
            leftIcon={<HiOutlineMail size='24px' />}
            loadingText='Sending Invitation'
            type='submit'
          >
            Send Invitation
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
